import {FunctionalComponent, h, reactive, readonly, shallowReactive, Slot, watchEffect,} from "vue";

export interface DataInput<T> {
  // 唯一标识
  id: string;
  // 数据
  data: T;
  // 打开状态
  opened?: boolean;
  // 选中状态
  selected?: boolean;
  // 选中状态，只有叶子节点会生效，默认未选中
  checked?: boolean;
  // 子节点
  children?: DataInput<T>[];
}

export interface DataNode<T> {
  id: string;
  // 层级
  level: number;
  // 数据
  data: T;
  // 打开状态
  opened: boolean;
  // 选中状态
  selected: boolean;
  // 目录选中状态
  checked: "yes" | "no" | "half";
  // 子节点
  children: DataNode<T>[];
  // 父节点
  parent?: DataNode<T>;
  // 要清除的副作用
  effects: (() => void)[];

  // 添加子项
  addChild(data: DataInput<T>): void;

  // 删除本项
  del(): void;

  // 勾选节点
  check(param: boolean): void;

  // 选中节点
  select(param: boolean): void;

  // 切换打开关闭
  setOpen(param: boolean): void;
}

export class TreeUtil<T> {
  private data = shallowReactive<{ root?: DataNode<T> }>({});

  private readonly record: Record<string, DataNode<T>> = {};

  static mapNode<NodeData, K>(
    data: K,
    transform: (data: K) => { input: DataInput<NodeData>; children: K[] }
  ) {
    const { input, children } = transform(data);
    input.children = children.map((child) => this.mapNode(child, transform));
    return input;
  }

  /**
   * 当前选中的节点id
   * @private
   */
  private currentSelectedId: string | undefined = undefined;

  public setData(input: DataInput<T>) {
    if (this.data.root) {
      this.del(this.data.root);
    }
    this.data.root = this.addNode(input);
  }

  public setOpenedAll(opened: boolean) {
    for (const node of Object.values(this.record)) {
      node.opened = opened;
    }
  }

  public openNode(id: string) {
    let node = this.record[id]?.parent;
    while (node) {
      node.opened = true;
      node = node.parent;
    }
  }

  public getSelectedNode() {
    return this.currentSelectedId
      ? this.record[this.currentSelectedId]
      : undefined;
  }

  /**
   * 渲染函数
   * @param slot
   */
  public render(slot: Slot | undefined) {
    return slot && this.data.root
      ? h(this.getComponent(slot), { node: this.data.root })
      : null;
  }

  /**
   * 添加节点
   * @param input
   * @param parent
   * @param index
   */
  private addNode(input: DataInput<T>, parent?: DataNode<T>, index?: number) {
    const node = reactive<DataNode<T>>({
      id: input.id,
      level: 0,
      data: input.data,
      opened: !!input.opened,
      selected: false,
      checked: "no",
      parent: parent,
      children: [],
      effects: [],
      addChild: (data) => this.addNode(data, node),
      del: () => this.del(node),
      check: (param: boolean) => this.check(node, param),
      select: (param: boolean) => this.select(node, param),
      setOpen: (param: boolean) => (node.opened = param),
    }) as DataNode<T>;
    // 根据子节点决定本节点的选中状态
    node.effects.push(
      watchEffect(() => {
        if (node.children.length === 0) {
          return;
        }
        let checkedNum = 0;
        for (const child of node.children) {
          if (child.checked === "half") {
            node.checked = "half";
            return;
          } else if (child.checked === "yes") {
            ++checkedNum;
          }
        }
        node.checked =
          checkedNum === 0
            ? "no"
            : checkedNum === node.children.length
            ? "yes"
            : "half";
      })
    );
    node.effects.push(
      watchEffect(() => {
        if (node.parent) {
          node.level = node.parent.level + 1;
        }
      })
    );
    // 添加到record
    this.record[node.id] = node;
    // 如果有子节点，添加子节点
    if (input.children) {
      input.children.map((child) => node.addChild(child));
    }
    if (parent) {
      if (typeof index === "number") {
        parent.children.splice(index, 0, node);
      } else {
        parent.children.push(node);
      }
    }
    // 根据输入状态
    node.check(input.checked || false);
    node.select(input.selected || false);
    return node;
  }

  /**
   * 删除节点
   * @param node
   * @private
   */
  private del(node: DataNode<T>) {
    this.removeParent(node);
    this.detach(node);
  }

  /**
   * 删除父对象
   * @param node
   * @private
   */
  private removeParent(node: DataNode<T>) {
    const parent = node.parent;
    if (parent) {
      parent.children.splice(parent.children.indexOf(node), 1);
      node.parent = undefined;
    } else {
      this.data.root = undefined;
    }
  }

  /**
   * 选择节点
   * @param node
   * @param param
   * @private
   */
  private select(node: DataNode<T>, param: boolean) {
    const current = this.currentSelectedId
      ? this.record[this.currentSelectedId]
      : undefined;
    if (node.id === current?.id) {
      if (!param) {
        node.selected = false;
        this.currentSelectedId = undefined;
      }
    } else {
      if (param) {
        node.selected = true;
        this.currentSelectedId = node.id;
        if (current) {
          current.selected = false;
        }
      }
    }
  }

  /**
   * 选中节点
   * @param checked
   * @param node
   * @private
   */
  private check(node: DataNode<T>, checked: boolean) {
    if (node.children.length === 0) {
      node.checked = checked ? "yes" : "no";
    } else {
      node.children.map((child) => {
        child.check(checked);
      });
    }
  }

  /**
   * 根据插槽返回函数组件
   * @param slot
   * @private
   */
  private getComponent(slot: Slot) {
    const component: FunctionalComponent<{ node: DataNode<any> }> = (props) => {
      return slot({
        node: props.node,
        children: props.node.children.map((child) =>
          h(component, { node: child })
        ),
      });
    };
    return component;
  }

  /**
   * 在移除节点时清理副作用
   * @param node
   * @private
   */
  private detach(node: DataNode<T>) {
    delete this.record[node.id];
    node.effects.map((effect) => effect());
    node.children.map((child) => this.detach(child));
  }

  public getRoot() {
    if (this.data.root) {
      return readonly<DataNode<T>>(this.data.root);
    }
    return undefined;
  }

  public nodeToInput(node: DataNode<T>): DataInput<T> {
    return {
      id: node.id,
      data: node.data,
      opened: node.opened,
      selected: node.selected,
      checked: node.checked === "yes",
      children: node.children.map((child) => this.nodeToInput(child)),
    };
  }

  private getNode(id: string) {
    const node = this.record[id];
    if (!node) {
      throw new Error("节点不存在");
    }
    return node;
  }

  public checkMove(
    nodeId: string,
    targetNodeId: string,
    pos: "before" | "after" | "inside"
  ) {
    const node = this.getNode(nodeId);
    const target = this.getNode(targetNodeId);
    // 移动目标的父节点
    let targetParent: DataNode<T>;
    // 移动目标的index
    let index: number;
    if (pos === "inside") {
      targetParent = target;
      index = targetParent.children.length;
    } else {
      if (!target.parent) {
        return { error: "RootLevel", message: "不能移动到root节点平级" };
      }
      targetParent = target.parent;
      if (pos === "before") {
        index = targetParent.children.indexOf(target);
      } else {
        index = targetParent.children.indexOf(target) + 1;
      }
    }
    if (targetParent === node) {
      return { error: "Self", message: "不能移动到自身子节点" };
    }
    // 判断目标父节点是否是当前节点的子节点
    let temp = targetParent;
    while (temp.level > node.level && temp.parent) {
      temp = temp.parent;
    }
    if (temp === node) {
      return { error: "SelfChildren", message: "不能移动到自身后代节点" };
    }
    return {
      error: null,
      message: "成功",
      data: { index, targetParent, node },
    };
  }

  public moveNodeTo(
    nodeId: string,
    targetNodeId: string,
    pos: "before" | "after" | "inside"
  ) {
    const res = this.checkMove(nodeId, targetNodeId, pos);
    if (!res.data) {
      return res;
    }
    const { targetParent, index, node } = res.data;
    this.removeParent(node);
    node.parent = targetParent;
    targetParent.children.splice(index, 0, node);
  }
}
